import { useLens } from "@hookform/lenses";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Dialog,
  MenuItem,
} from "@mui/material";
import { ReactElement, useMemo } from "react";
import { useForm } from "react-hook-form";

import {
  makeEditOwnedPhoneNumberFormSchema,
  PhoneNumberMessageClassValue,
} from "@stw/yup-validators";

import {
  EditOwnedPhoneNumberInput,
  PhoneNumberMessageClass,
  ShortCodeType,
} from "~frontend/__generated__/graphql";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfErrorBanner,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import {
  DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES,
  ShortCodeConfigForm,
  shortCodeConfigToFormValues,
  shortCodeConfigFormValuesToConfig,
} from "./ShortCodeConfigForm";
import {
  EditOwnedPhoneNumberFormValues,
  OwnedPhoneNumberForList,
} from "./types";

export default function EditOwnedPhoneNumberDialog({
  ownedPhoneNumber,
  onClose,
  onSubmit,
}: {
  ownedPhoneNumber: OwnedPhoneNumberForList;
  onClose: () => void;
  onSubmit: (values: EditOwnedPhoneNumberInput) => Promise<void>;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const isStaff = useCurrentUserIsStaff();

  const isShortCode =
    ownedPhoneNumber.messageClass === PhoneNumberMessageClass.ShortCode;
  const showShortCodeConfig = isStaff && !!ownedPhoneNumber.shortCodeConfig;

  const optInNumberSharesConfigWithOtherNumbers =
    ownedPhoneNumber.optInGroup?.ownedPhoneNumbers.some(
      opn => opn.id !== ownedPhoneNumber.id
    );

  const schema = useMemo(
    () =>
      makeEditOwnedPhoneNumberFormSchema({
        isStaff,
        messageClass:
          ownedPhoneNumber.messageClass as PhoneNumberMessageClassValue,
        hasExistingTcrCampaignId: !!ownedPhoneNumber.tenDlcCampaignId,
        hasShortCodeConfig: showShortCodeConfig,
      }),
    [
      isStaff,
      ownedPhoneNumber.messageClass,
      ownedPhoneNumber.tenDlcCampaignId,
      showShortCodeConfig,
    ]
  );

  const { control, handleSubmit, formState, setError, reset } =
    useForm<EditOwnedPhoneNumberFormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        displayName: ownedPhoneNumber.displayName ?? "",
        tcrCampaignId: ownedPhoneNumber.tenDlcCampaignId ?? "",
        shortCodeConfig: ownedPhoneNumber.shortCodeConfig
          ? shortCodeConfigToFormValues(ownedPhoneNumber.shortCodeConfig)
          : DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES,
        state: ownedPhoneNumber.state ?? "NEW",
        shortCodeType: ownedPhoneNumber.shortCodeType ?? "",
      },
      mode: "onBlur",
    });

  const lens = useLens({ control });

  async function handleFormSubmit(
    values: EditOwnedPhoneNumberFormValues
  ): Promise<void> {
    try {
      await onSubmit({
        displayName: values.displayName || null,
        ownedPhoneNumberId: ownedPhoneNumber.id,
        tcrCampaignId: values.tcrCampaignId || null,
        shortCodeConfig: showShortCodeConfig
          ? shortCodeConfigFormValuesToConfig(values.shortCodeConfig)
          : null,
        state: isStaff && isShortCode ? values.state : undefined,
        shortCodeType:
          isStaff && isShortCode
            ? (values.shortCodeType as ShortCodeType)
            : undefined,
      });
      reset();
    } catch (e) {
      setError("root", { message: getErrorMessage(e) });
    }
  }

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>Edit Phone Number</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          <RhfErrorBanner control={control} />
          <RhfTextField
            control={control}
            name="displayName"
            label="Display Name"
            fullWidth
          />

          {showShortCodeConfig && (
            <ShortCodeConfigForm
              lens={lens.focus("shortCodeConfig")}
              showSharedConfigWarning={optInNumberSharesConfigWithOtherNumbers}
              showShortCodeWarning={isShortCode}
            />
          )}

          {isStaff &&
            ownedPhoneNumber.messageClass ===
              PhoneNumberMessageClass.TenDlc && (
              <RhfTextField
                control={control}
                name="tcrCampaignId"
                label="TCR Campaign ID"
                fullWidth
              />
            )}

          {isStaff && isShortCode && (
            <RhfSelect control={control} name="state" label="Status" fullWidth>
              <MenuItem value="NEW">
                Pending (not usable by clients, can be tested by carriers)
              </MenuItem>
              <MenuItem value="ACTIVE">
                Active (approved by carriers and ready for client use)
              </MenuItem>
            </RhfSelect>
          )}

          {isStaff && isShortCode && (
            <RhfSelect
              control={control}
              name="shortCodeType"
              label="Short Code Type"
              fullWidth
            >
              <MenuItem value={ShortCodeType.Regular}>Regular</MenuItem>
              <MenuItem value={ShortCodeType.Vanity}>Vanity</MenuItem>
            </RhfSelect>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            disabled={formState.isSubmitting}
            onClick={onClose}
            color="secondary"
          >
            Cancel
          </Button>

          <SubmitButton
            isSubmitting={formState.isSubmitting}
            disabled={formState.isSubmitting}
            color="primary"
            variant="contained"
            submitText="Update"
            loadingText="Updating..."
            data-testid="edit-owned-phone-number-display-name-button"
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
